import { HttpError } from '@/lib/httpError'

interface User {
  id: string
  name: string
  account: string
  type: 'local' | 'ldap' | 'oidc'
  platform_role: 'owner' | 'admin' | 'auditor' | 'security' | 'user'
  contract_agreement: boolean | null
  totp_enabled: boolean
  last_login_at?: string
  created_at: string
  updated_at: string
}

export interface ProjectMember {
  id: string
  project_id: string
  user_id: string
  role: 'owner' | 'admin' | 'member' | 'viewer' | 'observer'
  created_at: string
  updated_at: string
  user: User
}

export interface ProjectMembersResponse {
  limit: number
  offset: number
  members: ProjectMember[]
  total: number
}

export interface AddProjectMemberData {
  user_id: string
  role: 'owner' | 'admin' | 'member' | 'viewer' | 'observer'
}

class ProjectMemberService {
  private baseUrl = '/api/v2/projects'

  async getProjectMembers(projectId: string, offset: number = 0, limit: number = 100): Promise<ProjectMembersResponse> {
    const response = await fetch(`${this.baseUrl}/${projectId}/members?offset=${offset}&limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to fetch project members', response.status)
    }

    return response.json()
  }

  async addMember(projectId: string, data: AddProjectMemberData): Promise<ProjectMember> {
    const response = await fetch(`${this.baseUrl}/${projectId}/members`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to add project member', response.status)
    }

    return response.json()
  }

  async requestAddMember(projectId: string, data: AddProjectMemberData): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${projectId}/members/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to submit member addition request', response.status)
    }
  }

  async updateMemberRole(projectId: string, userId: string, role: 'owner' | 'admin' | 'member' | 'viewer' | 'observer'): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${projectId}/members/${userId}/role`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ role }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to update member role', response.status)
    }
  }

  async requestMemberRoleChange(projectId: string, userId: string, role: 'owner' | 'admin' | 'member' | 'viewer' | 'observer'): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${projectId}/members/${userId}/role/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ role }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to submit role change request', response.status)
    }
  }

  async removeMember(projectId: string, userId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${projectId}/members/${userId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to remove member', response.status)
    }
  }

  async requestMemberRemoval(projectId: string, userId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${projectId}/members/${userId}/remove/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to submit member removal request', response.status)
    }
  }
}

export const projectMemberService = new ProjectMemberService()
